import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create the Cart Context
const CartContext = createContext(null);

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartAnimating, setIsCartAnimating] = useState(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('StuffLab-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('StuffLab-cart');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('StuffLab-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart
  const addToCart = useCallback((item) => {
    setCartItems((prevItems) => {
      // Check if item already exists with same id, size, and color
      const existingIndex = prevItems.findIndex(
        (cartItem) =>
          cartItem.id === item.id &&
          cartItem.selectedSize === item.selectedSize &&
          cartItem.selectedColor?.name === item.selectedColor?.name
      );

      if (existingIndex > -1) {
        // Update quantity if item exists
        const updatedItems = [...prevItems];
        updatedItems[existingIndex] = {
          ...updatedItems[existingIndex],
          quantity: updatedItems[existingIndex].quantity + (item.quantity || 1),
        };
        return updatedItems;
      }

      // Add new item with all required fields
      return [
        ...prevItems,
        {
          id: item.id,
          name: item.name,
          price: item.price,
          image: item.image || item.imageUrl,
          selectedSize: item.selectedSize || null,
          selectedColor: item.selectedColor || null,
          quantity: item.quantity || 1,
          brand: item.brand || 'StuffLab',
          rating: item.rating,
          reviews: item.reviews,
        },
      ];
    });

    // Trigger cart animation
    setIsCartAnimating(true);
    setTimeout(() => setIsCartAnimating(false), 600);
  }, []);

  // Remove item from cart
  const removeFromCart = useCallback((itemId, selectedSize, selectedColor) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          !(
            item.id === itemId &&
            item.selectedSize === selectedSize &&
            item.selectedColor?.name === selectedColor?.name
          )
      )
    );
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((itemId, selectedSize, selectedColor, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId, selectedSize, selectedColor);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId &&
        item.selectedSize === selectedSize &&
        item.selectedColor?.name === selectedColor?.name
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  }, [removeFromCart]);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // Calculate cart totals
  const cartSubtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const cartItemsCount = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  // Shipping estimate (free over $50, otherwise $5.99)
  const shippingEstimate = cartSubtotal >= 50 ? 0 : cartSubtotal > 0 ? 5.99 : 0;

  // Tax estimate (8% tax rate)
  const taxEstimate = cartSubtotal * 0.08;

  // Total
  const cartTotal = cartSubtotal + shippingEstimate + taxEstimate;

  const value = {
    cartItems,
    cartItemsCount,
    cartSubtotal,
    shippingEstimate,
    taxEstimate,
    cartTotal,
    isCartAnimating,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
